import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@lato/client/components/ui/button";
import { Input } from "@lato/client/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@lato/client/components/ui/dialog";
import { useAdminCreateOrg, useAdminOrganizations } from "@/hooks/useAdmin";
import { SidebarAvatar, initialsOf } from "@/features/dashboard/shared";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

function CreateWorkspaceDialog() {
  const create = useAdminCreateOrg();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);

  const effectiveSlug = slugEdited ? slug : slugify(name);
  const valid = name.trim().length > 0 && /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/.test(effectiveSlug);

  const submit = async () => {
    try {
      await create.mutateAsync({ name: name.trim(), slug: effectiveSlug });
      toast.success(`Workspace "${name.trim()}" created`);
      setOpen(false);
      setName("");
      setSlug("");
      setSlugEdited(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create workspace");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1.5 h-4 w-4" /> New workspace
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create workspace</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Capital"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Slug</label>
            <Input
              value={effectiveSlug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value);
              }}
              placeholder="acme"
            />
            <p className="text-[11px] text-muted-foreground/70">
              Lowercase letters, digits, hyphens. 2–32 chars.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => void submit()}>
            {create.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Workspaces() {
  const { data: orgs, isLoading } = useAdminOrganizations();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // A workspace opens its management page (members, invites, profile). Its studies live in the one
  // studies module, filtered to this organization — the admin never switches org to see them.
  const openWorkspace = (org: { id: string }) => {
    void navigate({ to: "/admin/workspaces/$orgId", params: { orgId: org.id } });
  };

  return (
    // The admin layout's content panel never scrolls (same as _agents); this page owns its scroll.
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-[28px] font-light leading-tight tracking-tight text-foreground">
            Workspaces
          </h1>
          <CreateWorkspaceDialog />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !orgs || orgs.length === 0 ? (
          <div className="rounded-xl border border-border/60 bg-card py-12 text-center text-sm text-muted-foreground">
            No workspaces yet. Create one to get started.
          </div>
        ) : (
          <div className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-card">
            {orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => openWorkspace(org)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/30"
              >
                <SidebarAvatar
                  src={org.avatar_url ?? ""}
                  initials={initialsOf(org.name)}
                  alt={org.name}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{org.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {org.slug}
                    {org.domain ? ` · ${org.domain}` : ""}
                  </div>
                </div>
                <div className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                  {org.member_count} {org.member_count === 1 ? "member" : "members"}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_auth/admin/workspaces/")({
  component: Workspaces,
});
